<template>
  <!-- Ten sam adres obsługuje listę i podgląd pojedynczego dokumentu — przełącza je
       obecność ?id= w adresie. Link do dokumentu da się wysłać dalej i po odświeżeniu
       otworzy się to samo. -->
  <SubiektDocumentDetails v-if="detailId" />

  <div v-else>
    <!-- Nagłówek w układzie modułu zamówień: ścieżka po lewej, licznik po prawej. -->
    <div class="hidden md:flex justify-between items-center">
      <SubiektBreadcrumb current="Dokumenty do Subiekta" />
      <div class="flex items-center gap-4">
        <BridgeStatus :status="bridge" :queued="queuedCount" />
        <div v-if="!loading" class="flex gap-1 select-none text-sm border-b-2 border-gray-300 leading-8">
          <span class="font-semibold uppercase text-gray-500">Dokumentów:</span>
          <span class="select-text font-bold text-indigo-500">{{ documents.length }}</span>
        </div>
      </div>
    </div>
    <div class="flex md:hidden justify-between items-center mb-2">
      <router-link to="/dashboard/orders" class="text-sm font-medium text-blue-500 hover:text-blue-700">&larr; Cofnij</router-link>
      <BridgeStatus :status="bridge" :queued="queuedCount" />
    </div>

    <!-- PASEK NARZĘDZI -->
    <div class="flex flex-col gap-4 py-5 xl:flex-row xl:items-end xl:justify-between">
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-1">
          <button @click="shiftDay(-1)" type="button" v-tooltip="'Poprzedni dzień'"
            class="transition duration-300 inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700">
            <ChevronLeftIcon class="h-4 w-4" aria-hidden="true" />
          </button>
          <div class="relative rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
            <label for="subiekt-dzien" class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Dzień</label>
            <input id="subiekt-dzien" v-model="selectedDate" @change="loadDocuments()" type="date"
              class="block h-9 border-0 bg-transparent px-3 text-sm text-gray-900 focus:ring-0" />
          </div>
          <button @click="shiftDay(1)" type="button" v-tooltip="'Następny dzień'"
            class="transition duration-300 inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700">
            <ChevronRightIcon class="h-4 w-4" aria-hidden="true" />
          </button>
          <button v-if="selectedDate !== todayText" @click="goToday()" type="button"
            class="transition duration-300 ml-1 inline-flex h-9 items-center rounded-md px-2.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">
            Dziś
          </button>
          <!-- Kalendarz dni, w których są dokumenty - szybszy niż przeklikiwanie dat. -->
          <button @click="showDaysModal = true" type="button" v-tooltip="'Pokaż dni, w których są dokumenty'"
            class="transition-all duration-300 ml-1 inline-flex h-9 items-center gap-x-1.5 rounded-md bg-indigo-100 px-2.5 text-xs font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200">
            <CalendarIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
            Utworzone dokumenty
          </button>
        </div>

        <div class="relative rounded-md focus-within:border-indigo-600 focus-within:ring-1 focus-within:ring-indigo-600">
          <label for="subiekt-rodzaj" class="absolute -top-2 left-2 -mt-px inline-block rounded-t-md bg-gradient-to-t from-white to-gray-100 px-1 text-xs font-medium text-gray-900">Rodzaj</label>
          <select id="subiekt-rodzaj" v-model="typeFilter" @change="rememberFilter()"
            class="block h-9 w-full rounded-md border-gray-300 py-0 pl-3 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500">
            <option value="all">Wszystkie</option>
            <option v-for="section in sections" :key="section.action" :value="section.action">
              {{ section.typ }} · {{ section.title }}
            </option>
          </select>
        </div>

        <div class="relative">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <SearchIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
          </div>
          <input v-model="query" type="search" placeholder="Szukaj: tytuł, kontrahent, numer"
            class="block h-9 w-56 rounded-md border border-gray-300 bg-white pl-9 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <router-link to="/dashboard/subiekt/ustawienia" v-tooltip="'Ustawienia i kartoteka kontrahentów'">
          <button type="button" aria-label="Ustawienia i kontrahenci"
            class="transition-all duration-300 inline-flex h-9 w-9 items-center justify-center rounded-md bg-indigo-100 text-indigo-600 shadow-sm hover:bg-indigo-200">
            <CogIcon class="h-5 w-5" aria-hidden="true" />
          </button>
        </router-link>
        <button @click="showRoutePicker = true" type="button"
          class="transition-all duration-300 inline-flex h-9 items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500">
          <PlusIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
          Utwórz wydania z tras
        </button>
      </div>
    </div>

    <!-- Liczniki dnia. Kafelek jest filtrem: klik zawęża listę do tego stanu,
         drugi klik zdejmuje filtr. Kolor paska = kolor paska przy wierszach. -->
    <div class="mb-6 grid grid-cols-2 gap-3 select-none sm:grid-cols-3 lg:grid-cols-5">
      <button v-for="tile in statusChips" :key="tile.key" @click="toggleStatus(tile.key)" type="button"
        :class="[statusFilter === tile.key ? 'ring-2 ring-indigo-500' : 'hover:shadow-md',
                 tile.border, 'transition-all duration-200 rounded-lg border-l-4 bg-white px-4 py-3 text-left shadow']">
        <p class="text-xs font-semibold uppercase text-gray-500">{{ tile.label }}</p>
        <p :class="[tile.count > 0 ? tile.text : 'text-gray-300', 'mt-1 text-2xl font-semibold doc-tabular']">{{ tile.count }}</p>
      </button>
    </div>
    <div v-if="statusFilter !== 'all' || typeFilter !== 'all' || query" class="-mt-3 mb-4">
      <button @click="resetFilters()" type="button" class="text-xs font-medium text-red-500 transition duration-200 hover:text-red-700">&#10005; Wyczyść filtry</button>
    </div>

    <!-- Komunikaty stanu systemu - tylko gdy coś faktycznie nie działa. -->
    <div v-if="sendingEnabled === false" class="mb-4 flex gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-2.5">
      <ExclamationIcon class="h-5 w-5 flex-shrink-0 text-yellow-500" aria-hidden="true" />
      <p class="text-sm text-yellow-800">
        Wysyłka do Subiekta jest wyłączona (<span class="font-mono text-xs">Configs/subiekt.enabled</span>) —
        dokumenty można składać i sprawdzać, ale nic nie trafi do kolejki.
      </p>
    </div>
    <div v-else-if="bridge && !bridge.online && queuedCount > 0" class="mb-4 flex gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-2.5">
      <StatusOfflineIcon class="h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
      <p class="text-sm text-red-800">
        {{ queuedCount }} {{ queuedCount === 1 ? 'dokument czeka' : 'dokumentów czeka' }} w kolejce, ale most do Subiekta
        nie odzywa się{{ bridge.ostatniKontaktUnix ? ` od ${formatContact(bridge.ostatniKontaktUnix)}` : '' }}.
        Sprawdź usługę mostu na serwerze z Subiektem — dokumenty pójdą same, gdy wróci.
      </p>
    </div>
    <div v-if="skipped && skipped.listyBezPozycji > 0" class="mb-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-2.5 text-sm text-yellow-800">
      {{ skipped.listyBezPozycji }} z {{ skipped.listyZnalezione }} {{ skipped.listyZnalezione === 1 ? 'listy przyjęć' : 'list przyjęć' }}
      nie dało żadnej pozycji — produkty na nich nie mają symbolu Subiekta.
    </div>

    <!-- LISTA: osobna karta na każdy rodzaj dokumentu. -->
    <div v-if="loading" class="space-y-6">
      <div v-for="index in 2" :key="`skeleton-${index}`" class="overflow-hidden rounded-lg border bg-white shadow">
        <div class="flex items-center gap-3 border-b px-5 py-4">
          <div class="h-10 w-10 animate-pulse rounded-lg bg-gray-200"></div>
          <div><div class="h-3.5 w-48 animate-pulse rounded-full bg-gray-200"></div><div class="mt-2 h-3 w-72 animate-pulse rounded-full bg-gray-100"></div></div>
        </div>
        <div v-for="row in 3" :key="row" class="flex items-center gap-6 border-b border-gray-100 px-5 py-4 last:border-0">
          <div class="h-3.5 w-64 animate-pulse rounded-full bg-gray-200"></div>
          <div class="h-3 w-32 animate-pulse rounded-full bg-gray-100"></div>
          <div class="ml-auto h-6 w-28 animate-pulse rounded-md bg-gray-100"></div>
        </div>
      </div>
    </div>

    <div v-else-if="loadError" class="rounded-lg border bg-white px-6 py-12 text-center shadow">
      <ExclamationIcon class="mx-auto h-8 w-8 text-red-400" aria-hidden="true" />
      <p class="mt-2 text-sm text-gray-700">{{ loadError }}</p>
      <button @click="loadDocuments()" type="button"
        class="mt-3 inline-flex h-9 items-center rounded-md bg-indigo-100 px-3 text-xs font-semibold text-indigo-600 hover:bg-indigo-200">Spróbuj ponownie</button>
    </div>

    <div v-else-if="visibleSections.length === 0" class="rounded-lg border bg-white px-6 py-14 text-center shadow">
      <DocumentTextIcon class="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
      <template v-if="documents.length === 0">
        <p class="mt-3 text-sm font-semibold text-gray-900">Brak dokumentów z {{ formattedDateShort }}</p>
        <p class="mt-1 text-sm text-gray-500">Wydania powstają z tras — wybierz trasy, a system złoży po jednym dokumencie na kontrahenta.</p>
        <button @click="showRoutePicker = true" type="button"
          class="mt-4 inline-flex h-9 items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500">
          <PlusIcon class="-ml-0.5 h-4 w-4" aria-hidden="true" />
          Utwórz wydania z tras
        </button>
      </template>
      <template v-else>
        <p class="mt-3 text-sm font-semibold text-gray-900">Żaden dokument nie pasuje do filtrów</p>
        <button @click="resetFilters()" type="button" class="mt-2 text-xs font-semibold text-indigo-600 hover:text-indigo-500">Wyczyść filtry</button>
      </template>
    </div>

    <div v-else class="space-y-6">
      <section v-for="section in visibleSections" :key="section.action" class="overflow-hidden rounded-lg border bg-white shadow">
        <!-- Nagłówek karty: czym są te dokumenty i co z nimi zrobić. -->
        <header class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div class="flex min-w-0 items-center gap-3">
            <span :class="[sectionTile(section.typ), 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold']">{{ section.typ }}</span>
            <div class="min-w-0">
              <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-900">
                {{ section.title }}
                <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 doc-tabular">{{ section.items.length }}</span>
              </h2>
              <p class="mt-0.5 truncate text-xs text-gray-500">{{ section.opis }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button v-if="sectionReady(section).length > 0" @click="sendAllReady(section)" :disabled="!sendingEnabled || bulkSending" type="button"
              :class="[(!sendingEnabled || bulkSending) ? 'opacity-50 pointer-events-none' : 'hover:bg-indigo-200',
                       'transition-all duration-300 inline-flex h-8 items-center gap-x-1.5 rounded-md bg-indigo-100 px-2.5 text-xs font-semibold text-indigo-600']">
              <RefreshIcon v-if="bulkSending" class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              <PaperAirplaneIcon v-else class="h-3.5 w-3.5 transform rotate-90" aria-hidden="true" />
              Wyślij gotowe ({{ sectionReady(section).length }})
            </button>
            <Menu v-if="section.items.length > 1" as="div" class="relative inline-block text-left">
              <MenuButton class="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-600">
                <span class="sr-only">Opcje sekcji</span>
                <DotsVerticalIcon class="h-5 w-5" aria-hidden="true" />
              </MenuButton>
              <MenuItems class="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <MenuItem v-slot="{ active }">
                  <a @click="deleteSection(section)" :class="[active ? 'bg-red-50 text-red-700' : 'text-red-600', 'group flex cursor-pointer items-center px-4 py-2 text-sm']">
                    <TrashIcon class="mr-3 h-5 w-5 text-red-400" aria-hidden="true" />
                    Usuń wszystkie z sekcji
                  </a>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </header>

        <div class="overflow-x-auto">
          <!-- table-fixed + te same szerokości w każdej karcie: kolumny stoją w jednej
               linii między sekcjami, a długi tekst się ucina zamiast rozpychać stronę. -->
          <table class="w-full table-fixed">
            <colgroup>
              <col />
              <col class="hidden w-56 lg:table-column" />
              <col class="hidden w-24 md:table-column" />
              <col class="w-20" />
              <col class="w-56" />
              <col class="w-40" />
              <col class="w-28" />
            </colgroup>
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-2.5 pl-5 pr-3 text-left text-xs font-semibold uppercase text-gray-500">Dokument</th>
                <th scope="col" class="hidden px-3 py-2.5 text-left text-xs font-semibold uppercase text-gray-500 lg:table-cell">Kontrahent</th>
                <th scope="col" class="hidden px-3 py-2.5 text-left text-xs font-semibold uppercase text-gray-500 md:table-cell">Data</th>
                <th scope="col" class="px-3 py-2.5 text-right text-xs font-semibold uppercase text-gray-500">Pozycje</th>
                <th scope="col" class="px-3 py-2.5 text-left text-xs font-semibold uppercase text-gray-500">Uwagi</th>
                <th scope="col" class="px-3 py-2.5 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                <th scope="col" class="py-2.5 pl-3 pr-5"><span class="sr-only">Czynności</span></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="doc in section.items" :key="doc.id" @click="goToDocument(doc)"
                class="cursor-pointer transition duration-150 hover:bg-indigo-50">
                <!-- Kolorowy pasek przy krawędzi = stan dokumentu, widoczny bez czytania. -->
                <td :class="[statusOf(doc).stripe, 'border-l-4 py-3 pl-4 pr-3']">
                  <p class="truncate text-sm font-semibold text-gray-900" :title="doc.tytul">{{ doc.tytul }}</p>
                  <p v-if="doc.podtytul" class="mt-0.5 truncate text-xs text-gray-500" :title="doc.podtytul">{{ doc.podtytul }}</p>
                </td>
                <td class="hidden px-3 py-3 lg:table-cell">
                  <p class="truncate text-xs font-semibold text-gray-700" :title="contractorName(doc)">{{ contractorName(doc) }}</p>
                </td>
                <td class="hidden whitespace-nowrap px-3 py-3 text-xs font-semibold text-gray-600 md:table-cell">{{ documentDate(doc) }}</td>
                <td class="whitespace-nowrap px-3 py-3 text-right">
                  <button v-if="doc.towary && doc.towary.length > 0" @click.stop="previewDocument = doc" type="button" v-tooltip="'Podgląd pozycji'"
                    class="doc-tabular inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 transition duration-200 hover:bg-gray-200 hover:text-gray-900">
                    <ViewListIcon class="h-3.5 w-3.5" aria-hidden="true" />{{ doc.towary.length }}
                  </button>
                  <span v-else class="text-xs text-gray-400">0</span>
                </td>
                <!-- Co wymaga uwagi - krótkie znaczniki, pełna treść w podpowiedzi.
                     Dzięki temu każdy wiersz ma tę samą wysokość. -->
                <td class="px-3 py-3">
                  <div class="flex min-w-0 flex-wrap gap-1">
                    <span v-for="note in notesOf(doc)" :key="note.text" v-tooltip="note.tooltip"
                      :class="[note.cls, 'inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-xs font-medium']">
                      <component :is="note.icon" class="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      <span class="truncate">{{ note.text }}</span>
                    </span>
                    <span v-if="notesOf(doc).length === 0" class="text-xs text-gray-300">—</span>
                  </div>
                </td>
                <td class="whitespace-nowrap px-3 py-3">
                  <span v-tooltip="statusOf(doc).tooltip"
                    :class="[statusOf(doc).badge, 'inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold']">
                    <RefreshIcon v-if="statusOf(doc).spin" class="h-3 w-3 animate-spin" aria-hidden="true" />
                    <span class="truncate">{{ statusOf(doc).text }}</span>
                  </span>
                </td>
                <td class="whitespace-nowrap py-3 pl-3 pr-5 text-right" @click.stop>
                  <div class="flex items-center justify-end gap-1.5">
                    <button v-if="canSend(doc)" @click="sendDocument(doc)"
                      :disabled="sendingId != null || !sendingEnabled || hasBlockers(doc)" type="button" v-tooltip="sendTooltip(doc)"
                      :class="[(sendingId != null || !sendingEnabled || hasBlockers(doc)) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-indigo-500',
                               'transition-all duration-300 inline-flex h-7 items-center gap-x-1 rounded-md bg-indigo-600 px-2.5 text-xs font-semibold text-white']">
                      <RefreshIcon v-if="sendingId === doc.id" class="h-3 w-3 animate-spin" aria-hidden="true" />
                      {{ sendingId === doc.id ? 'Wysyłam…' : 'Wyślij' }}
                    </button>
                    <Menu as="div" class="relative inline-block text-left">
                      <MenuButton class="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-600">
                        <span class="sr-only">Opcje</span>
                        <DotsVerticalIcon class="h-5 w-5" aria-hidden="true" />
                      </MenuButton>
                      <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-50 mt-2 w-52 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div class="py-1">
                            <MenuItem v-slot="{ active }">
                              <a @click="goToDocument(doc)" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex cursor-pointer items-center px-4 py-2 text-sm']">
                                <DocumentTextIcon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />Otwórz dokument
                              </a>
                            </MenuItem>
                            <MenuItem v-if="doc.towary && doc.towary.length > 0" v-slot="{ active }">
                              <a @click="previewDocument = doc" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex cursor-pointer items-center px-4 py-2 text-sm']">
                                <ViewListIcon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />Podgląd pozycji
                              </a>
                            </MenuItem>
                            <MenuItem v-if="doc.status === 'WYSLANY'" v-slot="{ active }">
                              <a @click="revertDocument(doc)" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex cursor-pointer items-center px-4 py-2 text-sm']">
                                <ReplyIcon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />Wycofaj z kolejki
                              </a>
                            </MenuItem>
                          </div>
                          <div class="py-1">
                            <MenuItem v-slot="{ active }">
                              <a @click="deleteDocument(doc)" :class="[active ? 'bg-red-50 text-red-700' : 'text-red-600', 'group flex cursor-pointer items-center px-4 py-2 text-sm']">
                                <TrashIcon class="mr-3 h-5 w-5 text-red-400" aria-hidden="true" />Usuń z panelu
                              </a>
                            </MenuItem>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <p v-if="!loading && documents.length > 0" class="mt-3 text-xs text-gray-400">
      {{ formattedDate }} · kliknij wiersz, żeby otworzyć dokument.
    </p>

    <SubiektDaysModal
      v-if="showDaysModal"
      :current="selectedDate"
      @close="showDaysModal = false"
      @pick="openDay"
    />

    <SubiektRouteSelectModal
      v-if="showRoutePicker"
      :date="selectedDate"
      @close="showRoutePicker = false"
      @created="applyCreated"
    />

    <SubiektDocumentModal
      v-if="previewDocument"
      :document="previewDocument"
      :sendingEnabled="sendingEnabled === true"
      :warehouses="warehouses"
      :sending="sendingId === previewDocument.id"
      @close="previewDocument = null"
      @send="sendDocument"
      @goto="goToDocument"
    />
  </div>
</template>

<script>
import { subiektPost } from '@/subiektApi';
import moment from 'moment';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import {
  ExclamationIcon, RefreshIcon, PlusIcon, SearchIcon, CogIcon, ChevronLeftIcon, ChevronRightIcon,
  DocumentTextIcon, ViewListIcon, DotsVerticalIcon, TrashIcon, ReplyIcon, XCircleIcon, ArchiveIcon,
  PaperAirplaneIcon, StatusOfflineIcon, InformationCircleIcon, CalendarIcon,
} from '@heroicons/vue/solid';
import SubiektDaysModal from '@/components/SubiektDaysModal.vue';
import SubiektDocumentModal from '@/components/SubiektDocumentModal.vue';
import SubiektRouteSelectModal from '@/components/SubiektRouteSelectModal.vue';
import SubiektDocumentDetails from '@/components/SubiektDocumentDetails.vue';
import BridgeStatus from '@/components/SubiektBridgeStatus.vue';
import SubiektBreadcrumb from '@/components/SubiektBreadcrumb.vue';
import { subiektStatus, typeBadge, shortageText, contractorName } from '@/subiektUi';
import {
  getLastDate, setLastDate, getLastTab, setLastTab,
  getCachedDocuments, setCachedDocuments, invalidateDocuments,
} from '@/subiektDocumentsCache';

// Akordówka (produkcja i naprawy) jest schowana, dopóki nie będzie potrzebna.
// Widok jest nadal osiągalny pod /dashboard/subiekt/produkcja.
const POKAZ_AKORDOWKE = false;

// Kolejność sekcji = kolejność pracy księgowości: wydania, braki do nich,
// przyjęcia od dostawców, zwroty.
const SECTIONS = [
  { action: 'WZ', typ: 'WZ', title: 'Wydania na trasy', opis: 'wybrane trasy → jeden dokument na kontrahenta' },
  { action: 'WZ_FAKTURA', typ: 'WZ', title: 'Wydania fakturowane', opis: 'kontrahenci rozliczani fakturą — wydanie i tak wychodzi stąd' },
  { action: 'PW_BRAKI', typ: 'PW', title: 'Przyjęcia na braki', opis: 'przyjmuje to, czego zabrakło na dzień wydania' },
  { action: 'WZ_UZUPELNIENIE', typ: 'WZ', title: 'Uzupełnienia wydań', opis: 'pozycje pominięte przez brak — pójdą po swoim PW' },
  { action: 'PZ_ZAKUP', typ: 'PZ', title: 'Przyjęcia od dostawców', opis: 'z numerem i cenami z faktury dostawcy' },
  { action: 'FZ_ZAKUP', typ: 'FZ', title: 'Faktury zakupu', opis: 'na podstawie PZ, powiązane z e-fakturą z KSeF — powstają same po wystawieniu PZ' },
  { action: 'PZ_ZWROT', typ: 'PZ', title: 'Zwroty i reklamacje', opis: 'wycena z ostatniej ceny zakupu' },
];
if (POKAZ_AKORDOWKE) SECTIONS.push({ action: 'PRODUKCJA', typ: 'RW·PW·MM', title: 'Produkcja i naprawy', opis: 'idą do Subiekta po kolei' });

function normalize(text) {
  return (text || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export default {
  name: 'SubiektDocuments',
  components: {
    Menu, MenuButton, MenuItems, MenuItem,
    ExclamationIcon, RefreshIcon, PlusIcon, SearchIcon, CogIcon, ChevronLeftIcon, ChevronRightIcon,
    DocumentTextIcon, ViewListIcon, DotsVerticalIcon, TrashIcon, ReplyIcon, XCircleIcon, ArchiveIcon,
    PaperAirplaneIcon, StatusOfflineIcon, InformationCircleIcon, CalendarIcon, SubiektDaysModal,
    SubiektDocumentModal, SubiektRouteSelectModal, SubiektDocumentDetails, BridgeStatus, SubiektBreadcrumb,
  },
  data() {
    const remembered = getLastTab() || {};
    return {
      // Dzień wybrany przed wejściem w podgląd — widok montuje się od nowa po powrocie.
      selectedDate: getLastDate() || moment().format('YYYY-MM-DD'),
      loading: true,
      loadError: '',
      documents: [],
      // null = jeszcze nie wiadomo. Baner o wyłączonej wysyłce pokazujemy dopiero po
      // odpowiedzi backendu - inaczej mignąłby przy każdym wejściu na dzień.
      sendingEnabled: null,
      bridge: null,
      warehouses: {},
      skipped: null,
      sendingId: null,
      bulkSending: false,
      deletingId: null,
      requestSeq: 0,
      previewDocument: null,
      showRoutePicker: false,
      showDaysModal: false,
      pollTimer: null,
      typeFilter: typeof remembered === 'object' && remembered.type ? remembered.type : 'all',
      statusFilter: typeof remembered === 'object' && remembered.status ? remembered.status : 'all',
      query: '',
    };
  },
  computed: {
    detailId() {
      return (this.$route.query.id || '').toString().trim();
    },
    todayText() {
      return moment().format('YYYY-MM-DD');
    },
    formattedDate() {
      return moment(this.selectedDate, 'YYYY-MM-DD').format('dddd, D MMMM YYYY');
    },
    formattedDateShort() {
      return moment(this.selectedDate, 'YYYY-MM-DD').format('D MMMM YYYY');
    },
    sections() {
      return SECTIONS;
    },
    queuedCount() {
      return this.documents.filter(d => d.status === 'WYSLANY' || d.status === 'W_TRAKCIE').length;
    },
    statusChips() {
      const count = (key) => this.documents.filter(d => this.statusKey(d) === key).length;
      return [
        { key: 'ready', label: 'Do wysłania', count: count('ready'), border: 'border-indigo-500', text: 'text-indigo-600' },
        { key: 'blocked', label: 'Do uzupełnienia', count: count('blocked'), border: 'border-yellow-400', text: 'text-yellow-600' },
        { key: 'queued', label: 'W kolejce', count: count('queued'), border: 'border-blue-500', text: 'text-blue-600' },
        { key: 'done', label: 'Wystawione', count: count('done'), border: 'border-green-500', text: 'text-green-600' },
        { key: 'error', label: 'Z błędem', count: count('error'), border: 'border-red-500', text: 'text-red-600' },
      ];
    },
    filteredDocuments() {
      const phrase = normalize(this.query.trim());
      return this.documents.filter(doc => {
        if (this.typeFilter !== 'all' && doc.grupa !== this.typeFilter) return false;
        if (this.statusFilter !== 'all' && this.statusKey(doc) !== this.statusFilter) return false;
        if (phrase.length === 0) return true;
        return [doc.tytul, doc.podtytul, contractorName(doc), doc.subiekt && doc.subiekt.numer, doc.numerFaktury, doc.id]
          .some(value => normalize(value).includes(phrase));
      });
    },
    visibleSections() {
      return SECTIONS
        .map(section => ({ ...section, items: this.filteredDocuments.filter(d => d.grupa === section.action) }))
        .filter(section => section.items.length > 0);
    },

  },
  watch: {
    // Odpytujemy tylko, gdy coś czeka na most - inaczej timer nie chodzi wcale.
    queuedCount: {
      immediate: true,
      handler(count) {
        this.stopPolling();
        if (count > 0) this.pollTimer = setInterval(() => this.loadDocuments({ silent: true }), 8000);
      },
    },
    // Powrót z podglądu nie przemontowuje tego komponentu, a dokument mógł w międzyczasie
    // zostać wysłany - bez tego lista pokazywałaby stary status.
    detailId(current, previous) {
      if (current.length === 0 && previous.length > 0) this.loadDocuments();
    },
  },
  methods: {
    typeBadge,
    shortageText,
    contractorName,
    sectionReady(section) {
      return section.items.filter(doc => this.canSend(doc) && !this.hasBlockers(doc));
    },
    // Kafelek typu w nagłówku karty - ten sam kolor co znacznik typu w dokumencie.
    sectionTile(typ) {
      if (typ === 'WZ') return 'bg-blue-100 text-blue-700';
      if (typ === 'PZ') return 'bg-green-100 text-green-700';
      if (typ === 'PW') return 'bg-purple-100 text-purple-700';
      if (typ === 'FZ') return 'bg-yellow-100 text-yellow-800';
      return 'bg-gray-100 text-gray-700';
    },
    // Krótkie znaczniki do kolumny „Uwagi" - pełna treść w podpowiedzi.
    notesOf(doc) {
      const notes = [];
      if (doc.blad) notes.push({ text: 'Błąd wysyłki', tooltip: doc.blad, icon: 'XCircleIcon', cls: 'bg-red-50 text-red-700' });
      const blockers = Array.isArray(doc.blokady) ? doc.blokady : [];
      if (blockers.length > 0 && !doc.tylkoPorownanie) {
        notes.push({
          text: blockers.length === 1 ? blockers[0] : `${blockers.length} rzeczy do uzupełnienia`,
          tooltip: blockers.join(' • '), icon: 'ExclamationIcon', cls: 'bg-yellow-50 text-yellow-800',
        });
      }
      if (Array.isArray(doc.braki) && doc.braki.length > 0) {
        notes.push({ text: `Braki: ${shortageText(doc)}`, tooltip: `Na dzień dokumentu zabrakło: ${shortageText(doc)}`, icon: 'ArchiveIcon', cls: 'bg-orange-50 text-orange-800' });
      }
      if (doc.ostrzezenie) notes.push({ text: 'Uwaga', tooltip: doc.ostrzezenie, icon: 'ExclamationIcon', cls: 'bg-yellow-50 text-yellow-800' });
      if (doc.tylkoPorownanie) {
        notes.push({ text: 'Stara wersja', tooltip: 'Dokument złożony przez poprzednią wersję systemu — przebuduje się po wdrożeniu nowej.', icon: 'InformationCircleIcon', cls: 'bg-gray-100 text-gray-600' });
      }
      return notes;
    },
    statusOf(doc) {
      return subiektStatus(doc);
    },
    statusKey(doc) {
      return subiektStatus(doc).key;
    },
    canSend(doc) {
      if (doc.tylkoPorownanie) return false;
      return !['WYSTAWIONY', 'WYSLANY', 'W_TRAKCIE'].includes(doc.status);
    },
    hasBlockers(doc) {
      return Array.isArray(doc.blokady) && doc.blokady.length > 0;
    },
    sendTooltip(doc) {
      if (!this.sendingEnabled) return 'Wysyłka do Subiekta jest wyłączona';
      if (this.hasBlockers(doc)) return doc.blokady.join(' ');
      return 'Wstaw do kolejki — most wystawi dokument w Subiekcie';
    },
    documentDate(doc) {
      const unix = Number(doc.documentDateUnix);
      if (unix > 0) return moment.unix(unix).format('DD.MM.YYYY');
      return doc.dzien ? moment(doc.dzien, 'YYYY-MM-DD').format('DD.MM.YYYY') : '—';
    },
    formatContact(unix) {
      const contact = moment.unix(unix);
      return contact.isSame(moment(), 'day') ? contact.format('HH:mm') : contact.format('DD.MM HH:mm');
    },
    rememberFilter() {
      setLastTab({ type: this.typeFilter, status: this.statusFilter });
    },
    toggleStatus(key) {
      this.statusFilter = this.statusFilter === key ? 'all' : key;
      this.rememberFilter();
    },
    resetFilters() {
      this.typeFilter = 'all';
      this.statusFilter = 'all';
      this.query = '';
      this.rememberFilter();
    },
    shiftDay(days) {
      this.selectedDate = moment(this.selectedDate, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD');
      this.loadDocuments();
    },
    // Dzień wybrany w kalendarzu „Utworzone dokumenty". Filtry zdejmujemy, żeby
    // od razu było widać wszystko, co w tym dniu jest.
    openDay(day) {
      this.showDaysModal = false;
      this.resetFilters();
      this.selectedDate = day;
      this.loadDocuments();
    },
    goToday() {
      this.selectedDate = this.todayText;
      this.loadDocuments();
    },
    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    },
    goToDocument(doc) {
      this.previewDocument = null;
      this.$router.push({ path: '/dashboard/subiekt/dokumenty', query: { id: doc.id } });
    },
    notify(success, head, subheader) {
      this.$store.commit('setNotification', { show: true, head, subheader: subheader || '', success });
    },
    errorText(error, fallback) {
      return typeof error === 'string' ? error : fallback;
    },
    applyDocuments(payload) {
      this.documents = payload.dokumenty;
      this.sendingEnabled = payload.wysylkaWlaczona;
      this.bridge = payload.most || null;
      this.warehouses = payload.magazyny || {};
      this.skipped = payload.pominiete;
    },
    // Okno tras zwraca to, co przyszło z backendu. Dokument dostaje datę wskazaną
    // w oknie, która nie musi być dniem otwartym w panelu - wtedy przełączamy dzień,
    // inaczej utworzone dokumenty zniknęłyby przy pierwszym odświeżeniu.
    applyCreated(payload, documentDate) {
      if (documentDate && documentDate !== this.selectedDate) {
        this.selectedDate = documentDate;
        setLastDate(documentDate);
      }
      setCachedDocuments(this.selectedDate, payload);
      this.applyDocuments(payload);
    },
    async loadDocuments({ silent = false } = {}) {
      const date = this.selectedDate;
      setLastDate(date);

      // Wygrywa tylko ostatnie żądanie - zmiana dnia w trakcie składania zostawia
      // kilka w locie, a starsza odpowiedź nie może nadpisać nowszego dnia.
      const requestSeq = ++this.requestSeq;
      const current = () => this.requestSeq === requestSeq;

      const cached = getCachedDocuments(date);
      if (cached && !silent) {
        this.applyDocuments(cached);
        this.loading = false;
        this.loadError = '';
        this.loadDocuments({ silent: true });
        return;
      }

      if (!silent) this.loading = true;
      this.loadError = '';
      try {
        // Pusta lista tras: wejście na dzień tylko WCZYTUJE istniejące wydania,
        // nie składa nowych ze wszystkich tras dnia.
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/documents', { date, trasy: [] });
        if (!res.data.success) throw res.data.log || res.data.error;

        const payload = {
          dokumenty: res.data.dokumenty || [],
          wysylkaWlaczona: res.data.wysylkaWlaczona === true,
          most: res.data.most || null,
          magazyny: res.data.magazyny || {},
          pominiete: res.data.pominiete || null,
        };
        setCachedDocuments(date, payload);
        if (current()) this.applyDocuments(payload);
      } catch (error) {
        console.log(error);
        if (!silent && current()) this.loadError = this.errorText(error, 'Nie udało się pobrać dokumentów.');
      } finally {
        if (!silent && current()) this.loading = false;
      }
    },
    async postSend(doc) {
      const res = await subiektPost(this.$store.state.apiLink, '/subiekt/send', {
        id: doc.id,
        user: this.$store.state.userData,
      });
      if (!res.data.success) throw res.data.log || res.data.error;
      return res.data.log;
    },
    async sendDocument(doc) {
      if (this.sendingId) return;
      this.sendingId = doc.id;
      try {
        const log = await this.postSend(doc);
        this.notify(true, 'Dokument w kolejce do Subiekta.', log);
        this.previewDocument = null;
        invalidateDocuments(this.selectedDate);
        await this.loadDocuments();
      } catch (error) {
        console.log(error);
        this.notify(false, 'Nie udało się wysłać dokumentu.', this.errorText(error, 'Coś poszło nie tak.'));
      } finally {
        this.sendingId = null;
      }
    },
    // Wysyłka hurtem idzie PO KOLEI, w kolejności tabeli - to ta sama kolejność,
    // w której most je wystawi (przyjęcia na braki przed uzupełnieniami).
    async sendAllReady(section) {
      const items = this.sectionReady(section);
      if (items.length === 0 || this.bulkSending) return;
      if (!window.confirm(`Wstawić do kolejki ${items.length} dokumentów? Most wystawi je w Subiekcie po kolei.`)) return;

      this.bulkSending = true;
      const failed = [];
      for (const doc of items) {
        this.sendingId = doc.id;
        try {
          await this.postSend(doc);
        } catch (error) {
          failed.push(`${doc.tytul}: ${this.errorText(error, 'błąd')}`);
        }
      }
      this.sendingId = null;
      this.bulkSending = false;
      invalidateDocuments(this.selectedDate);
      await this.loadDocuments();
      if (failed.length === 0) this.notify(true, `W kolejce: ${items.length} dokumentów.`, 'Most wystawi je w ciągu kilku minut.');
      else this.notify(false, `Nie poszło ${failed.length} z ${items.length}.`, failed.join(' · '));
    },
    async revertDocument(doc) {
      if (!window.confirm(`Wycofać „${doc.tytul}" z kolejki? Wróci do szkicu.`)) return;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/document/cofnij', {
          id: doc.id,
          user: this.$store.state.userData,
        });
        if (!res.data.success) throw res.data.log || res.data.error;
        this.notify(true, 'Dokument wycofany.', res.data.log);
        invalidateDocuments(this.selectedDate);
        await this.loadDocuments();
      } catch (error) {
        console.log(error);
        this.notify(false, 'Nie udało się wycofać dokumentu.', this.errorText(error, 'Coś poszło nie tak.'));
      }
    },
    // Skasowania nie da się cofnąć, a przy dokumencie, który opuścił panel, usunięcie
    // znaczy co innego, niż się wydaje - stąd różne treści potwierdzenia.
    confirmText(doc) {
      const number = doc.subiekt && doc.subiekt.numer;
      if (number) {
        return `Usunąć „${doc.tytul}" z panelu?\n\nDokument jest wystawiony w Subiekcie jako ${number} i TAM ZOSTANIE — zniknie tylko z tej listy.`;
      }
      if (['WYSLANY', 'W_TRAKCIE'].includes(doc.status)) {
        return `Usunąć „${doc.tytul}" z panelu?\n\nDokument jest w kolejce mostu. Wycofaj go najpierw z kolejki, jeśli nie ma trafić do Subiekta.`;
      }
      return `Usunąć dokument „${doc.tytul}"? Tej operacji nie da się cofnąć.`;
    },
    deleteDocument(doc) {
      if (this.deletingId) return;
      return this.sendDelete([doc], this.confirmText(doc), doc.id);
    },
    deleteSection(section) {
      if (this.deletingId) return;
      const items = section.items;
      const inSubiekt = items.filter(item => item.subiekt && item.subiekt.numer).length;
      const queued = items.filter(item => ['WYSLANY', 'W_TRAKCIE'].includes(item.status)).length;
      const text = [`Usunąć wszystkie dokumenty z sekcji „${section.title}" (${items.length})? Tej operacji nie da się cofnąć.`];
      if (inSubiekt > 0) text.push(`Wystawionych w Subiekcie: ${inSubiekt} — TAM ZOSTANĄ, znikną tylko z panelu.`);
      if (queued > 0) text.push(`W kolejce mostu: ${queued}.`);
      return this.sendDelete(items, text.join('\n\n'), section.action);
    },
    async sendDelete(items, text, marker) {
      if (!window.confirm(text)) return;
      const removedIds = new Set(items.map(item => item.id));
      this.deletingId = marker;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/document/usun', { ids: [...removedIds] });
        if (!res.data.success) throw res.data.log || res.data.error;
        this.documents = this.documents.filter(item => !removedIds.has(item.id));
        invalidateDocuments(this.selectedDate);
        this.notify(true, removedIds.size === 1 ? 'Dokument usunięty.' : `Usunięto dokumenty (${removedIds.size}).`, res.data.log);
      } catch (error) {
        console.log(error);
        this.notify(false, 'Nie udało się usunąć.', this.errorText(error, 'Coś poszło nie tak.'));
      } finally {
        this.deletingId = null;
      }
    },
  },
  created() {
    moment.locale('pl');
  },
  mounted() {
    if (!this.detailId) this.loadDocuments();
  },
  beforeUnmount() {
    this.stopPolling();
  },
};
</script>

<style scoped>
/* Cyfry o stałej szerokości - liczniki odświeżają się w tle i nie mogą skakać. */
.doc-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
